import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { Button, Chip, Divider, Paper, Stack, Typography } from '@mui/material';

import useCustomScheduleDestinations from '~/hooks/customerCommunication/useCustomScheduleDestinations';
import useCustomScheduleHolidayTypes from '~/hooks/customerCommunication/useCustomScheduleHolidayTypes';

import { isDestinationContentOptional } from '../../utils/heroPlannerScheduleUtils';

import ScheduleHeaderImageInput, { parseHeaderImageFormData } from './Inputs/ScheduleHeaderImageInput';
import ScheduleIntroSectionForm, { parseIntroSectionFormData } from './Inputs/ScheduleIntroSectionForm';
import ScheduleTypeOptionInput, { parseTypeOptionFormData } from './Inputs/ScheduleTypeOptionInput';
import ScheduleEmailContentForm, { parseScheduleEmailContentFormData } from './ScheduleEmailContentForm';

interface DestinationFormData {
  [key: string]: string;
  webImageId: string;
  mobileImageId: string;
  headerUrl: string;
  introHeading: string;
  introCopy: string;
  introCta: string;
  introCtaUrl: string;
  emailPreHeader: string;
  emailSubjectLine: string;
}

export interface DestinationFormRef {
  getFormData: () => Promise<DestinationFormData | null>;
  validateForms: () => boolean;
}

interface Props {
  brandId: string;
  countryGroupId: string;
  countryId: string;
  offerIds: Array<string>;
  schedule?: Partial<CustomerCommunication.CustomSchedule>;
  type?: string;
}

function hasHeaderContent(schedule?: Partial<CustomerCommunication.CustomSchedule>) {
  return !!(
    schedule?.introSection?.headerDesktopImage ||
    schedule?.introSection?.headerMobileImage ||
    schedule?.introSection?.headerUrl
  );
}

function hasIntroSectionContent(schedule?: Partial<CustomerCommunication.CustomSchedule>) {
  return !!(
    schedule?.introSection?.introHeading ||
    schedule?.introSection?.introCopy ||
    schedule?.introSection?.introCta ||
    schedule?.introSection?.introCtaUrl
  );
}

const DestinationContentForm = forwardRef<DestinationFormRef, Props>((props, ref) => {
  const { brandId, countryGroupId, countryId, offerIds, schedule, type } = props;

  const destinationFormRef = useRef<HTMLFormElement>(null);
  const headerImageFormRef = useRef<HTMLFormElement>(null);
  const introSectionFormRef = useRef<HTMLFormElement>(null);
  const emailContentFormRef = useRef<HTMLFormElement>(null);

  const [selectedOption, setSelectedOption] = useState(
    type === 'HOLIDAY_TYPE' ? schedule?.holidayType || '' : schedule?.destination || '',
  );
  const [webImageId, setWebImageId] = useState(schedule?.introSection?.headerDesktopImage || '');
  const [mobileImageId, setMobileImageId] = useState(schedule?.introSection?.headerMobileImage || '');
  const [headerUrl, setHeaderUrl] = useState(schedule?.introSection?.headerUrl || '');
  const [introHeading, setIntroHeading] = useState(schedule?.introSection?.introHeading || '');
  const [introCopy, setIntroCopy] = useState(schedule?.introSection?.introCopy || '');
  const [introCta, setIntroCta] = useState(schedule?.introSection?.introCta || '');
  const [introCtaUrl, setIntroCtaUrl] = useState(schedule?.introSection?.introCtaUrl || '');
  const [addHeader, setAddHeader] = useState(hasHeaderContent(schedule));
  const [addIntroSection, setAddIntroSection] = useState(hasIntroSectionContent(schedule));

  // Fetch type options - destinations/ holiday types
  const { dataReq: destinationsDataReq, fetchDestinations } = useCustomScheduleDestinations(brandId);
  const { dataReq: holidayTypesDataReq, fetchHolidayTypes } = useCustomScheduleHolidayTypes(brandId);

  const typeOptionConfig = useMemo(
    () =>
      type === 'HOLIDAY_TYPE'
        ? {
            name: 'holidayTypeId',
            label: 'Select holiday type',
            dataReq: holidayTypesDataReq,
            refetch: fetchHolidayTypes,
          }
        : {
            name: 'destinationId',
            label: 'Select destination',
            dataReq: destinationsDataReq,
            refetch: fetchDestinations,
          },
    [type, destinationsDataReq, holidayTypesDataReq, fetchDestinations, fetchHolidayTypes],
  );

  useEffect(() => {
    setSelectedOption(type === 'HOLIDAY_TYPE' ? schedule?.holidayType || '' : schedule?.destination || '');
  }, [schedule, type]);

  const typeOptionTitle = type === 'HOLIDAY_TYPE' ? 'Holiday Type' : 'Destination';
  const isHeaderAndIntroOptional = isDestinationContentOptional(type, brandId);

  useEffect(() => {
    setWebImageId(schedule?.introSection?.headerDesktopImage ?? '');
    setMobileImageId(schedule?.introSection?.headerMobileImage ?? '');
    setHeaderUrl(schedule?.introSection?.headerUrl ?? '');
    setIntroHeading(schedule?.introSection?.introHeading ?? '');
    setIntroCopy(schedule?.introSection?.introCopy ?? '');
    setIntroCta(schedule?.introSection?.introCta ?? '');
    setIntroCtaUrl(schedule?.introSection?.introCtaUrl ?? '');
    setAddHeader(hasHeaderContent(schedule));
    setAddIntroSection(hasIntroSectionContent(schedule));
  }, [schedule]);

  const handleToggleHeader = () => {
    if (addHeader) {
      setWebImageId('');
      setMobileImageId('');
      setHeaderUrl('');
    }
    setAddHeader(!addHeader);
  };

  const handleToggleIntroSection = () => {
    if (addIntroSection) {
      setIntroHeading('');
      setIntroCopy('');
      setIntroCta('');
      setIntroCtaUrl('');
    }
    setAddIntroSection(!addIntroSection);
  };

  const validateForms = useCallback(() => {
    const shouldValidateHeader = !isHeaderAndIntroOptional || addHeader;
    const shouldValidateIntroSection = !isHeaderAndIntroOptional || addIntroSection;

    return (
      destinationFormRef.current?.reportValidity() &&
      (!shouldValidateHeader || headerImageFormRef.current?.reportValidity()) &&
      (!shouldValidateIntroSection || introSectionFormRef.current?.reportValidity()) &&
      emailContentFormRef.current?.reportValidity()
    );
  }, [addHeader, addIntroSection, isHeaderAndIntroOptional]);

  const getFormData = useCallback(async () => {
    if (!validateForms()) {
      return null;
    }

    const shouldIncludeHeader = !isHeaderAndIntroOptional || addHeader;
    const shouldIncludeIntroSection = !isHeaderAndIntroOptional || addIntroSection;

    const typeOptionData = parseTypeOptionFormData(new FormData(destinationFormRef.current), typeOptionConfig.name);
    const headerImageData = shouldIncludeHeader
      ? parseHeaderImageFormData(new FormData(headerImageFormRef.current))
      : { webImageId: '', mobileImageId: '', headerUrl: '' };
    const introSectionData = shouldIncludeIntroSection
      ? parseIntroSectionFormData(new FormData(introSectionFormRef.current))
      : { introHeading: '', introCopy: '', introCta: '', introCtaUrl: '' };
    const emailContentData = parseScheduleEmailContentFormData(new FormData(emailContentFormRef.current));

    const formDataValues = {
      ...typeOptionData,
      ...headerImageData,
      ...introSectionData,
      ...emailContentData,
    };
    return formDataValues;
  }, [addHeader, addIntroSection, isHeaderAndIntroOptional, validateForms, typeOptionConfig]);

  useImperativeHandle(ref, () => ({
    getFormData,
    validateForms,
  }));

  return (
    <Stack direction="column" gap={3}>
      <Paper variant="outlined" sx={{ p: 2, position: 'relative' }}>
        <Stack direction="column" gap={2}>
          <Typography variant="h4">{typeOptionTitle}</Typography>
          <ScheduleTypeOptionInput
            ref={destinationFormRef}
            name={typeOptionConfig.name}
            label={typeOptionConfig.label}
            value={selectedOption}
            onChange={setSelectedOption}
            dataReq={typeOptionConfig.dataReq}
          />
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, position: 'relative' }}>
        <Stack direction="column" gap={2}>
          <Typography variant="h4">Email Content</Typography>
          <ScheduleEmailContentForm
            ref={emailContentFormRef}
            countryGroupId={countryGroupId}
            countryId={countryId}
            offerIds={offerIds}
            showPrefillButton={false}
            preHeaderDefaultValue={schedule?.emailPreheader}
            subjectLineDefaultValue={schedule?.emailSubjectLine}
          />
        </Stack>
      </Paper>

      {isHeaderAndIntroOptional && (
        <Stack direction="row" spacing={2}>
          <Button
            variant={addHeader ? 'outlined' : 'contained'}
            color={addHeader ? 'error' : 'primary'}
            size="small"
            onClick={handleToggleHeader}
          >
            {addHeader ? 'Remove Header' : 'Add Header'}
          </Button>

          <Button
            variant={addIntroSection ? 'outlined' : 'contained'}
            color={addIntroSection ? 'error' : 'primary'}
            size="small"
            onClick={handleToggleIntroSection}
          >
            {addIntroSection ? 'Remove Intro Section' : 'Add Intro Section'}
          </Button>
        </Stack>
      )}

      {(!isHeaderAndIntroOptional || addHeader) && (
        <Paper variant="outlined" sx={{ p: 2, position: 'relative' }}>
          <Stack direction="column" gap={2}>
            <Typography variant="h4">Header Image</Typography>
            <form ref={headerImageFormRef}>
              <ScheduleHeaderImageInput
                webImageId={webImageId}
                mobileImageId={mobileImageId}
                headerUrl={headerUrl}
                onWebImageIdChange={setWebImageId}
                onMobileImageIdChange={setMobileImageId}
                onHeaderUrlChange={setHeaderUrl}
                isOptional={isHeaderAndIntroOptional}
              />
            </form>
          </Stack>
        </Paper>
      )}

      {(!isHeaderAndIntroOptional || addIntroSection) && (
        <Paper variant="outlined" sx={{ p: 2, position: 'relative' }}>
          <Stack direction="column" gap={2}>
            <Typography variant="h4">Intro Section</Typography>
            <ScheduleIntroSectionForm
              ref={introSectionFormRef}
              introHeading={introHeading}
              introCopy={introCopy}
              introCta={introCta}
              introCtaUrl={introCtaUrl}
              onIntroHeadingChange={setIntroHeading}
              onIntroCopyChange={setIntroCopy}
              onIntroCtaChange={setIntroCta}
              onIntroCtaUrlChange={setIntroCtaUrl}
              isOptional={isHeaderAndIntroOptional}
            />
          </Stack>
        </Paper>
      )}

      <Divider>
        <Chip label="Additional Sections" />
      </Divider>
    </Stack>
  );
});

DestinationContentForm.displayName = 'DestinationContentForm';
export default DestinationContentForm;
